import * as React from "react"
import { type EntityParty, ObjectKind } from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgChevronDown } from "@digits-shared/components/SVGIcons/line/ChevronDown.svg"
import { IconSize } from "@digits-shared/components/UI/Icons/Icon"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import { useOnBodyClick } from "@digits-shared/hooks/useOnBodyClick"
import colors from "@digits-shared/themes/colors"
import fonts, { LabelText } from "@digits-shared/themes/typography"
import styled, { css } from "styled-components"
import { BULK_FIELD_WIDTH } from "src/frontend/components/OS/Applications/Shared/Accounting/BulkActions/types"
import { useTextIsOverflowing } from "src/frontend/hooks/useTextIsOverflowing"
import { PartyIcon } from "src/shared/components/PartyHover/PartyIcon"
import {
  type NewPartyEntity,
  type PartyWithNewEntitiesTypeaheadResult,
} from "src/shared/components/Typeahead/sharedTypeahead"
import { Typeahead } from "src/shared/components/Typeahead/Typeahead"
import { useTypeaheadState } from "src/shared/components/Typeahead/useTypeaheadState"

const InputGroup = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`

const SelectArrow = styled(SvgChevronDown)`
  position: absolute;
  right: 6px;
  width: 24px;
  height: 24px;
  ${svgPathStyles(colors.translucentSecondary80, 1.5)};
`

const SelectContainer = styled.div<{
  $muteText: boolean
  $disabled: boolean
}>`
  position: relative;
  width: ${BULK_FIELD_WIDTH}px;
  height: 32px;
  padding-left: 6px;
  padding-right: 32px;
  border: 1px solid ${colors.secondary30};
  background: ${colors.translucentWhite50};
  border-radius: 8px;

  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;

  ${fonts.scale.body};
  text-align: left;
  color: ${({ $muteText }) => ($muteText ? colors.translucentSecondary50 : colors.secondary)};
  font-weight: ${({ $muteText }) => ($muteText ? fonts.weight.roman : fonts.weight.heavy)};

  &:hover {
    cursor: pointer;
    color: ${colors.secondary};
    background: ${colors.white};
    border-color: ${colors.accentBlue};

    ${SelectArrow} {
      ${svgPathStyles(colors.secondary, 1.5)};
    }
  }

  &:focus {
    outline: 1px solid ${colors.accentBlue};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
`

const Name = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const PartyTypeahead: React.FC<{
  party: EntityParty | NewPartyEntity | undefined
  setParty: (party: EntityParty | NewPartyEntity | undefined) => void
  placeholder: string | undefined
  disabled: boolean
  customLabel?: string
}> = ({ party, setParty, placeholder, disabled, customLabel }) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const { textElementRef, isTextOverflowing } = useTextIsOverflowing<HTMLDivElement>()
  const { isTypeaheadShown, hideTypeahead, onClick, onKeyDown, ref } = useTypeaheadState(
    containerRef,
    disabled
  )

  useOnBodyClick(ref, hideTypeahead)

  const onResultSelected = React.useCallback(
    (data: PartyWithNewEntitiesTypeaheadResult) => {
      const { entity } = data
      setParty(entity)
      hideTypeahead()
    },
    [setParty, hideTypeahead]
  )

  const clearParty = React.useCallback(() => {
    setParty(undefined)
  }, [setParty])

  return (
    <InputGroup>
      <LabelText>{customLabel ? customLabel : "Party"}</LabelText>
      <DigitsTooltip content={party?.name || placeholder} disabled={!isTextOverflowing}>
        <SelectContainer
          $muteText={!party}
          $disabled={disabled}
          onClick={onClick}
          onKeyDown={onKeyDown}
          ref={containerRef}
        >
          {party && <PartyIcon party={party} size={IconSize.Small} />}
          <Name ref={textElementRef}>{party?.name || placeholder}</Name>

          <SelectArrow />

          {isTypeaheadShown && (
            <Typeahead
              ref={ref}
              kind={ObjectKind.Party}
              onResultSelected={onResultSelected}
              onClear={clearParty}
              allowAddNew
              close={hideTypeahead}
            />
          )}
        </SelectContainer>
      </DigitsTooltip>
    </InputGroup>
  )
}
